import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface DropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'start' | 'end';
  /** Open upwards — for menus anchored to the bottom of the viewport. */
  side?: 'bottom' | 'top';
  className?: string;
  menuClassName?: string;
  /** Controlled mode; omit for self-managed open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ITEM_SELECTOR = '[role="menuitem"]:not([disabled])';

/**
 * Click-to-open menu with outside-click, Escape and roving arrow-key focus.
 *
 * The trigger is rendered as-is (callers pass their own `<button>`), so the
 * wrapper stays out of the accessibility tree and never nests buttons.
 */
export function DropdownMenu({
  trigger,
  children,
  align = 'start',
  side = 'bottom',
  className,
  menuClassName,
  open: controlledOpen,
  onOpenChange,
}: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        containerRef.current?.querySelector<HTMLElement>('button, [tabindex]')?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen]);

  // Move focus into the menu once it is on screen.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      menuRef.current?.querySelector<HTMLElement>(ITEM_SELECTOR)?.focus();
    }, 10);
    return () => window.clearTimeout(timer);
  }, [open]);

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (!items.length) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (index + 1 + items.length) % items.length
            : (index - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the caller supplies the real button */}
      <div
        className="contents"
        onClick={() => setOpen(!open)}
        onKeyDown={onTriggerKeyDown}
        data-state={open ? 'open' : 'closed'}
      >
        {trigger}
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            initial={{ opacity: 0, y: side === 'top' ? 4 : -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: side === 'top' ? 4 : -4, scale: 0.98 }}
            transition={{ duration: 0.13, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'pap-pop absolute z-40 min-w-52 overflow-hidden p-1 outline-none',
              side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
              align === 'end' ? 'right-0' : 'left-0',
              menuClassName,
            )}
          >
            {typeof children === 'function' ? children(close) : children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export interface DropdownItemProps {
  onSelect?: () => void;
  icon?: ReactNode;
  /** Right-aligned hint: a shortcut, a count, a check. */
  trailing?: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  active?: boolean;
  children: ReactNode;
  className?: string;
}

export function DropdownItem({
  onSelect,
  icon,
  trailing,
  disabled,
  destructive,
  active,
  children,
  className,
}: DropdownItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
        'text-fg outline-none hover:bg-surface focus-visible:bg-surface',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:text-subtle',
        active && 'bg-surface font-medium',
        destructive && 'text-danger hover:bg-danger-soft focus-visible:bg-danger-soft',
        className,
      )}
    >
      {icon && (
        <span className="flex size-3.5 shrink-0 items-center justify-center text-subtle [&_svg]:size-3.5">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing && <span className="shrink-0 text-xs text-subtle">{trailing}</span>}
    </button>
  );
}

export function DropdownSeparator() {
  return <div className="my-1 h-px bg-border" role="separator" />;
}

export function DropdownLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-subtle">
      {children}
    </div>
  );
}
